/**
 * EventCountdownHeader Component
 *
 * Animated countdown timer displayed in header
 * Shows days, hours, minutes, seconds until event
 */

import { Event } from "@/types/events.types";
import React, { useEffect, useState } from "react";
import { FaCalendarDay, FaClock } from "react-icons/fa";

interface EventCountdownHeaderProps {
  event: Event;
  onClick?: () => void;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateTimeRemaining = (endDate: Date): TimeRemaining => {
  const now = new Date().getTime();
  const end = new Date(endDate).getTime();
  const difference = end - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
};

export const EventCountdownHeader: React.FC<EventCountdownHeaderProps> = ({
  event,
  onClick,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining(event.endDate)
  );
  const [isToday, setIsToday] = useState<boolean>(false);

  useEffect(() => {
    const updateCountdown = () => {
      const time = calculateTimeRemaining(event.endDate);
      setTimeRemaining(time);
      setIsToday(time.days === 0 && time.hours === 0 && time.minutes === 0);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Update every second

    return () => clearInterval(interval);
  }, [event.endDate]);

  // Don't show past events
  if (
    timeRemaining.days === 0 &&
    timeRemaining.hours === 0 &&
    timeRemaining.minutes === 0 &&
    timeRemaining.seconds === 0
  ) {
    return null;
  }

  return (
    <div
      className={`event-countdown-header ${
        isToday ? "event-countdown-today" : ""
      }`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      title="Click to view event details"
    >
      <div className="event-countdown-header-content">
        {isToday ? (
          <>
            <FaCalendarDay className="event-countdown-header-icon animate-bounce" />
            <div className="event-countdown-header-text">
              <span className="event-countdown-header-label">आज का दिन!</span>
              <span className="event-countdown-header-title">
                {event.title}
              </span>
            </div>
          </>
        ) : (
          <>
            <FaClock className="event-countdown-header-icon" />
            <div className="event-countdown-timer-grid">
              <div className="event-countdown-timer-unit">
                <span className="event-countdown-timer-value">
                  {timeRemaining.days}
                </span>
                <span className="event-countdown-timer-label">दिन</span>
              </div>
              <span className="event-countdown-timer-separator">:</span>
              <div className="event-countdown-timer-unit">
                <span className="event-countdown-timer-value">
                  {String(timeRemaining.hours).padStart(2, "0")}
                </span>
                <span className="event-countdown-timer-label">घंटे</span>
              </div>
              <span className="event-countdown-timer-separator">:</span>
              <div className="event-countdown-timer-unit">
                <span className="event-countdown-timer-value">
                  {String(timeRemaining.minutes).padStart(2, "0")}
                </span>
                <span className="event-countdown-timer-label">मिनट</span>
              </div>
              <span className="event-countdown-timer-separator">:</span>
              <div className="event-countdown-timer-unit">
                <span className="event-countdown-timer-value">
                  {String(timeRemaining.seconds).padStart(2, "0")}
                </span>
                <span className="event-countdown-timer-label">सेकंड</span>
              </div>
            </div>
            <div className="event-countdown-header-pulse" />
          </>
        )}
      </div>
    </div>
  );
};

export default EventCountdownHeader;
